import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useFeatureGate } from "@/hooks/useFeatureGate";
import { LockedFeature } from "@/components/shared/LockedFeature";
import { MainLayout } from "@/components/layout/MainLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  TrendingUp, BarChart3, DollarSign, Package,
  Calculator, FileText, Tag, Users, Warehouse, Zap, Brain, Sparkles
} from "lucide-react";
import { KarolHealthSummary } from "@/components/karol/KarolHealthSummary";
import { KarolAlertaPanel } from "@/components/karol/KarolAlertaPanel";

import { ForecastTab, ProfitBrainTab, WeeklyReportTab } from "@/components/intelligence/ForecastTabs";
import { CMVTab } from "@/components/intelligence/CMVTab";
import { MenuIntelligenceTab, HealthScoreTab, SmartPricingTab } from "@/components/intelligence/AnalysisTabs";
import { PredictiveCRMTab, PredictiveInventoryTab, MoneyLeakTab } from "@/components/intelligence/PredictiveTabs";
import { CampaignROITab } from "@/components/intelligence/CampaignROITab";

export default function IntelligencePage() {
  const gate = useFeatureGate("ia_operacional");
  const { authAxios, user } = useAuth();
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(tabFromUrl || "profit");

  useEffect(() => {
    if (tabFromUrl) setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  if (gate === "blocked") return <MainLayout><LockedFeature featureName="IA Operacional" addonId="ia_operacional" /></MainLayout>;

  return (
    <MainLayout>
    <div className="p-4 md:p-6 max-w-5xl mx-auto" data-testid="intelligence-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center">
          <Brain className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-800">IA Operacional</h1>
          <p className="text-sm text-gray-400">Decisões baseadas em dados reais do seu restaurante</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4 flex-wrap">
          <TabsTrigger value="profit" className="gap-1"><DollarSign className="w-3 h-3" /> Profit Brain</TabsTrigger>
          <TabsTrigger value="cmv" className="gap-1"><Calculator className="w-3 h-3" /> CMV</TabsTrigger>
          <TabsTrigger value="report" className="gap-1"><FileText className="w-3 h-3" /> Relatório</TabsTrigger>
          <TabsTrigger value="pricing" className="gap-1"><Tag className="w-3 h-3" /> Precificação</TabsTrigger>
          <TabsTrigger value="menu_intel" className="gap-1"><Package className="w-3 h-3" /> Menu Intel</TabsTrigger>
          <TabsTrigger value="health" className="gap-1"><BarChart3 className="w-3 h-3" /> Health Score</TabsTrigger>
          <TabsTrigger value="crm" className="gap-1">
            <Users className="w-3 h-3" /> CRM
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="gap-1">
            <TrendingUp className="w-3 h-3" /> Campanhas
          </TabsTrigger>
          <TabsTrigger value="inventory" className="gap-1 opacity-50 cursor-not-allowed" disabled>
            <Warehouse className="w-3 h-3" /> Inventário <span className="text-[9px] bg-gray-200 text-gray-500 px-1 py-0.5 rounded ml-0.5">em breve</span>
          </TabsTrigger>
          <TabsTrigger value="leaks" className="gap-1"><Zap className="w-3 h-3" /> Fugas</TabsTrigger>
          <TabsTrigger value="forecast" className="gap-1"><TrendingUp className="w-3 h-3" /> Previsão</TabsTrigger>
          <TabsTrigger value="karol" className="gap-1"><Sparkles className="w-3 h-3" /> Karol Central</TabsTrigger>
        </TabsList>

        <TabsContent value="profit"><ProfitBrainTab authAxios={authAxios} onNavigateTo={setActiveTab} /></TabsContent>
        <TabsContent value="cmv"><CMVTab authAxios={authAxios} /></TabsContent>
        <TabsContent value="report"><WeeklyReportTab authAxios={authAxios} /></TabsContent>
        <TabsContent value="pricing"><SmartPricingTab authAxios={authAxios} /></TabsContent>
        <TabsContent value="menu_intel"><MenuIntelligenceTab authAxios={authAxios} /></TabsContent>
        <TabsContent value="health"><HealthScoreTab authAxios={authAxios} /></TabsContent>
        <TabsContent value="crm"><PredictiveCRMTab authAxios={authAxios} /></TabsContent>
        <TabsContent value="campaigns"><CampaignROITab authAxios={authAxios} /></TabsContent>
        <TabsContent value="leaks"><MoneyLeakTab authAxios={authAxios} /></TabsContent>
        <TabsContent value="forecast"><ForecastTab authAxios={authAxios} /></TabsContent>
        <TabsContent value="karol">
          <KarolAlertaPanel authAxios={authAxios} restaurantId={user?.restaurant_id} />
          <KarolHealthSummary authAxios={authAxios} restaurantId={user?.restaurant_id} />
        </TabsContent>
      </Tabs>
    </div>
    </MainLayout>
  );
}
